import React, { useEffect, useState } from 'react';

import { link } from '@aline/routes';
import { Card, IconButton, Skeleton, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { CollapsibleText, EditPartyDialog, PartyRoutes, Spacer } from 'src/components';
import { BackArrowIcon } from 'src/components/icons/BackArrowIcon';
import { EditIcon } from 'src/components/icons/EditIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { OrganizationIcon } from 'src/components/icons/OrganizationIcon';
import type { Apollo } from 'src/gql/generated';
import { usePartyParams } from 'src/helpers';
import { useGetParty } from 'src/hooks';
import { useTabContext } from 'src/layouts/tabs/tab-context';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  allDocuments: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  backButton: {
    marginRight: theme.spacing(1),
  },
  card: {
    padding: theme.spacing(2),
  },
  cardBody: {
    display: 'flex',
  },
  cardHeader: {
    alignItems: 'flex-start',
    display: 'flex',
  },
  cardRightContent: {
    flexGrow: 1,
  },
  descriptionContainer: {
    width: '75%',
  },
  legalName: {
    marginBottom: theme.spacing(2),
  },
  nameContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  orgIcon: {
    height: 100,
    marginRight: theme.spacing(2),
    width: 100,
  },
  titleContainer: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing(4),
  },
}));

export function Party(): JSX.Element {
  const { classes } = useStyles();
  const { partyId } = usePartyParams();
  const { updateActiveTabTitle } = useTabContext();
  const { loading, party } = useGetParty(partyId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (typeof party?.name === 'string') updateActiveTabTitle(`${party.name || 'Untitled Party'}`);
  }, [party?.name, updateActiveTabTitle]);

  return (
    <>
      <Helmet>
        <title>{`${party?.name} - Party`}</title>
      </Helmet>
      <div className={classes.titleContainer}>
        <IconButton
          className={classes.backButton}
          component={Link}
          size="small"
          to={link.PartyIndex()}
        >
          <BackArrowIcon />
        </IconButton>
        <Typography variant="h2">Party</Typography>
      </div>
      <PartyCard loading={loading} party={party} />
      <div className={classes.allDocuments}>
        <PartyRoutes />
      </div>
    </>
  );
}

interface PartyCardProps {
  loading: boolean;
  party: Pick<Apollo.Party, 'description' | 'id' | 'name' | '__typename'> | null;
}

function PartyCard({ loading, party }: PartyCardProps): JSX.Element {
  const { classes } = useStyles();
  const [open, setOpen] = useState<boolean>(false);

  if (loading || !party)
    return (
      <Card className={classes.card}>
        <div className={classes.cardBody}>
          <OrganizationIcon className={classes.orgIcon} fill={IconColors.LightGray} />
          <div className={classes.cardRightContent}>
            <div className={classes.cardHeader}>
              <div className={classes.nameContainer}>
                <Typography>
                  <Skeleton width={100} />
                </Typography>
                <Typography className={classes.legalName} variant="subtitle1">
                  <Skeleton variant="text" width={150} />
                </Typography>
              </div>
              <Spacer />
              <IconButton disabled size="large">
                <EditIcon />
              </IconButton>
            </div>
            <Typography>
              <Skeleton variant="text" width={400} />
            </Typography>
            <Typography>
              <Skeleton variant="text" width={400} />
            </Typography>
          </div>
        </div>
      </Card>
    );

  return (
    <>
      <Card className={classes.card}>
        <div className={classes.cardBody}>
          <OrganizationIcon className={classes.orgIcon} fill={IconColors.LightGray} />
          <div className={classes.cardRightContent}>
            <div className={classes.cardHeader}>
              <div className={classes.nameContainer}>
                <Typography color="textSecondary">Legal Name</Typography>
                <Typography className={classes.legalName} variant="subtitle1">
                  {party.name || 'Untitled'}
                </Typography>
              </div>
              <Spacer />
              <IconButton size="small" onClick={() => setOpen(!open)}>
                <EditIcon />
              </IconButton>
            </div>
            <CollapsibleText className={classes.descriptionContainer} maxHeightPx={64}>
              <Typography>{party.description || 'No description'}</Typography>
            </CollapsibleText>
          </div>
        </div>
      </Card>
      <EditPartyDialog handleClose={() => setOpen(false)} open={open} partyId={party.id} />
    </>
  );
}
